/* Newsletter (Grow): one source piece, three output types.
 *
 * Her spec, 2026-09-17: "one module, one source piece of writing, three output
 * types... this is not three separate modules — it's one content object with a
 * `type` field." The BLOG is the canonical full version and IS the source; the
 * email and the Substack post are CUTS of it, each carrying `sourceId` and the
 * blog's URL as their canonical link.
 *
 * The cuts are not trims. The email may carry a section the blog does not (a
 * "what I'm working on", curated links) and the Substack post wants a different
 * opening rather than the same paragraphs with more removed — that is a prompt
 * job (api/_lib/prompts.js newsletter_copy), and this file only says which blocks
 * each type holds.
 *
 * WHY THE BLOG CARRIES SEO-SHAPED FIELDS: they are the fields GHL's own blog
 * API asks for (description, urlSlug, imageUrl, imageAltText, categories, tags,
 * canonicalLink, publishedAt), read from their docs so the push needs no
 * migration later. Nothing here pushes anything yet.
 */

import { useCallback, useMemo } from "react";
import { createRecordStore } from "./recordStore";
import { offerLine } from "./offerLine";
import { reorderWrites } from "./reorder";
import { applyLabelledBlock, ordinalWord, parseLabelledBlock, stripLabelledBlock } from "./sectionBlock";
import { wordCount } from "./websiteSectionKinds";

/* ── The three types ───────────────────────────────────────────────────────── */
export const NEWSLETTER_TYPES = [
  {
    id: "blog",
    label: "Blog post",
    hint: "The full piece — the version everything else is cut from.",
    /* WRITE FIRST (her call, 2026-11-03, docs/newsletter-write-first-spec.md):
       a new post opens on ONE blank page for her own words. The claims, FAQ,
       short answer and ask are added around them later by the Brill menu.
       A post made before this keeps its sections and its old screen. */
    blocks: ["writing"],
  },
  {
    id: "email",
    label: "Email",
    hint: "A shorter cut for the list, with room for what you're working on.",
    blocks: ["subject", "opening", "body", "ask"],
  },
  {
    id: "substack",
    label: "Substack post",
    hint: "The shortest cut, opening on a different angle.",
    blocks: ["opening", "body", "ask"],
  },
];

export const typeDef = (id) => NEWSLETTER_TYPES.find((t) => t.id === id) || NEWSLETTER_TYPES[0];
export const isSource = (n) => (n?.type || "blog") === "blog";
/** The two cuts a source can produce. */
export const VARIANT_TYPES = ["email", "substack"];

/* ── The blocks ────────────────────────────────────────────────────────────────
   One field each unless there is a reason: this is prose she reads top to
   bottom, and splitting a paragraph into three boxes fights that.

   CLAIMS and FAQ are the AI-search requirement, and they are hers to keep or
   delete: "a real tension with the archetype voice profile — needs a check, not
   an automatic override." So they are blocks like any other, and the check that
   notices they are missing lives in `claimsCheck` below, not in a guard that
   refuses to save. */
export const BLOCK_KINDS = [
  /* HER OWN WORDS, the whole piece in one place (2026-11-03). Never rewritten:
     the Brill menu only proposes fixes, cuts and additions she accepts one by
     one. A line starting "## " is a subhead between her paragraphs. Never
     offered under Add section — a write-first post has exactly one. */
  {
    id: "writing",
    label: "Your piece",
    hint: "Your words, the way you wrote them",
    once: true,
    hidden: true,
    types: ["blog"],
    fields: [
      { key: "subtitle", block: "SUBTITLE", label: "Subtitle", type: "line", guide: "one line under the title" },
      { key: "text", block: "WRITING", label: "Your piece", type: "prose", guide: "her own words, exactly as written" },
    ],
  },
  /* THE SHORT ANSWER, at the top of a blog post: two or three sentences built
     from her lines by Make it search friendly. Added, never offered. */
  {
    id: "answer",
    label: "The short answer",
    hint: "Two or three sentences at the top, from your own lines",
    once: true,
    hidden: true,
    types: ["blog"],
    fields: [
      { key: "text", block: "ANSWER", label: "The short answer", type: "prose", guide: "two or three sentences", words: 70 },
    ],
  },
  {
    id: "subject",
    label: "Subject line",
    hint: "The inbox line — and the preview under it",
    once: true,
    types: ["email"],
    fields: [
      { key: "subject", block: "SUBJECT", label: "Subject", type: "line", guide: "under 45 characters" },
      { key: "preview", block: "PREVIEW", label: "Preview line", type: "line", guide: "what shows after it" },
    ],
  },
  {
    id: "opening",
    label: "Opening",
    hint: "The first lines — your voice, a real moment",
    once: true,
    fields: [
      { key: "text", block: "OPENING", label: "Opening", type: "prose", guide: "the way you would say it out loud", words: 120 },
    ],
  },
  {
    id: "body",
    label: "Body",
    hint: "The middle — one point, made properly",
    fields: [
      { key: "heading", block: "HEADING", label: "Heading", type: "line", guide: "what this part is about", words: 8 },
      { key: "text", block: "BODY", label: "Body", type: "prose", guide: "the point, an example, what it means for them", words: 220 },
    ],
  },
  {
    id: "claims",
    label: "What this says",
    hint: "The claims someone could quote — two or three, stated plainly",
    once: true,
    types: ["blog"],
    fields: [
      { key: "text", block: "CLAIMS", label: "Claims", type: "prose", guide: "one per line, each one a whole thought", words: 90 },
    ],
  },
  {
    id: "faq",
    label: "Questions this answers",
    hint: "What they would type into ChatGPT — answered in a line or two each",
    once: true,
    types: ["blog"],
    fields: [
      { key: "text", block: "FAQ", label: "Questions and answers", type: "prose", guide: "Q on one line, the answer under it", words: 200 },
    ],
  },
  {
    id: "ask",
    label: "The ask",
    hint: "The end — one thing to do",
    once: true,
    fields: [
      { key: "text", block: "ASK", label: "The ask", type: "prose", guide: "one ask, said plainly", words: 40 },
    ],
  },
  {
    id: "custom",
    label: "Your own section",
    hint: "Name it and write it — what you're working on, links, anything",
    fields: [
      { key: "heading", block: "HEADING", label: "Heading", type: "line", guide: "what this part is about", words: 8 },
      { key: "text", block: "BODY", label: "Body", type: "prose", guide: "in your words", words: 180 },
    ],
  },
];

export const CUSTOM_KIND = "custom";

export function blockKindDef(id) {
  return BLOCK_KINDS.find((k) => k.id === id) || BLOCK_KINDS.find((k) => k.id === CUSTOM_KIND);
}

/** What a block is called on screen: her name, else its kind's. */
export function blockName(block) {
  return String(block?.name || "").trim() || blockKindDef(block?.blockType).label;
}

/** The kind as the shared Block and Drafter read it — its label is her name. */
export function blockKindOf(block) {
  const kind = blockKindDef(block?.blockType);
  return { ...kind, id: kind.id, label: blockName(block) };
}

/**
 * The kinds she can still add. A once-only kind already there is left out, and
 * a kind belonging to another TYPE is never offered — an email has no FAQ block
 * and a blog post has no subject line.
 */
export function addableBlockKinds(blocks = [], type = "blog") {
  const have = new Set((blocks || []).map((b) => b.blockType));
  return BLOCK_KINDS
    .filter((k) => !k.hidden)
    .filter((k) => !k.types || k.types.includes(type))
    .filter((k) => !k.once || !have.has(k.id));
}

/** A WRITE-FIRST post: one page of her own words (2026-11-03). A blog post
 *  with no sections yet counts too, so it opens on the blank page. */
export function isWritingPiece(newsletter, blocks = []) {
  if (!isSource(newsletter)) return false;
  return (blocks || []).length === 0 || (blocks || []).some((b) => b.blockType === "writing");
}

export function emptyBlockContent(kindId) {
  const out = {};
  for (const f of blockKindDef(kindId).fields) out[f.key] = "";
  return out;
}

/* ── Words and state ──────────────────────────────────────────────────────── */
export function blockWords(block) {
  const content = block?.content || {};
  return blockKindDef(block?.blockType).fields.reduce((n, f) => n + wordCount(content[f.key]), 0);
}

export function newsletterWords(blocks = []) {
  return (blocks || []).reduce((n, b) => n + blockWords(b), 0);
}

/** Roughly how long it takes to read — GHL asks for it, and it is computed,
 *  never stored: two copies of a number can disagree. */
export function readMinutes(blocks = []) {
  return Math.max(1, Math.round(newsletterWords(blocks) / 225));
}

export const NEWSLETTER_STATE_LABEL = { done: "Done", draft: "Draft", empty: "Not written" };

export function newsletterState(newsletter, blocks = []) {
  if (newsletter?.status === "done") return "done";
  return newsletterWords(blocks) > 0 ? "draft" : "empty";
}

export const blockState = (block) => (blockWords(block) > 0 ? "draft" : "empty");

export function newsletterLabel(n) {
  return String(n?.name || "").trim() || `Untitled ${typeDef(n?.type).label.toLowerCase()}`;
}

/* ── THE CLAIMS CHECK ──────────────────────────────────────────────────────────
   Her requirement, and the one thing here that can fail silently: "Brill should
   flag when a Blog Post draft is pure narrative with no extractable, quotable
   claims — this is a real tension with the archetype voice profile and needs a
   check, not an automatic override."

   So this reports; it never blocks a save and never rewrites her. A post with
   no claims and no FAQ looks perfectly fine on screen and simply never gets
   found, which is why the absence has to be SAID.

   Blog posts only. An email is not competing to be quoted by a search engine.
*/
export function claimsCheck(newsletter, blocks = []) {
  if (!isSource(newsletter)) return null;
  const words = newsletterWords(blocks);
  if (words === 0) return null;                 // nothing written yet is not a warning

  const has = (id) => (blocks || []).some((b) => b.blockType === id && blockWords(b) > 0);
  const missing = [];
  if (!has("claims")) missing.push("a claim someone could quote");
  if (!has("faq")) missing.push("the questions it answers");
  if (!String(newsletter?.pillar || "").trim()) missing.push("a content pillar");

  if (!missing.length) return null;
  return {
    missing,
    /* Said as what it costs her, not as a rule she broke. */
    text: missing.length === 3
      ? "This reads as a story with nothing quotable in it. Add what it says, the questions it answers, and which pillar it is — or leave it as a piece for people who already know you."
      : `Still missing ${missing.join(" and ")} — that is the part search engines and ChatGPT can actually use.`,
  };
}

/* ── Copy out ────────────────────────────────────────────────────────────────
   The route that works for everyone, GHL or not (her hard rule). Plain text,
   because it is pasted into Mailchimp, Kit, Substack's editor or an email. */
export function blockToText(block) {
  const kind = blockKindDef(block?.blockType);
  const content = block?.content || {};
  const lines = [];
  const heading = String(content.heading || "").trim();
  if (heading) lines.push(heading);
  for (const f of kind.fields) {
    if (f.key === "heading") continue;
    const v = String(content[f.key] || "").trim();
    if (!v) continue;
    lines.push(f.key === "subject" || f.key === "preview" ? `${f.label}: ${v}` : v);
  }
  return lines.join("\n\n");
}

export function newsletterToText(newsletter, blocks = []) {
  const parts = [newsletterLabel(newsletter)];
  for (const b of blocks || []) {
    const text = blockToText(b);
    if (text) parts.push(text);
  }
  /* A variant says where the full version lives — her "each variant links back
     to the blog post as the canonical/full version". */
  const canonical = String(newsletter?.canonicalUrl || "").trim();
  if (canonical && !isSource(newsletter)) parts.push(`The full version: ${canonical}`);
  return parts.join("\n\n");
}

/* ── How the list reads ───────────────────────────────────────────────────────
   Banded by type, sources first — the Offer Ladder's banding, and it says the
   shape out loud: the blog posts ARE the sources, the rest are cuts. A cut
   carries the name of the piece it came from as its meta, so the family is
   readable without nesting a table inside a table. */
export function groupNewsletters(newsletters = []) {
  return NEWSLETTER_TYPES
    .map((t) => ({
      key: t.id,
      label: `${t.label}s`,
      rows: (newsletters || []).filter((n) => (n.type || "blog") === t.id),
    }))
    .filter((g) => g.rows.length);
}

/**
 * Reordering inside ONE band, renumbering the whole list afterwards.
 *
 * The table and the rail are banded by type, so both report a move as an index
 * WITHIN its band — `[from, to, bandKey]`. Treating those as positions in the
 * flat list renumbers the wrong rows: dragging the second email to the top
 * would rewrite two blog posts instead.
 *
 * A drop into another band is ignored rather than silently changing the type,
 * which would strand the sections belonging to the old one. Lifted from
 * `scriptReorderWrites`, which draws the same line for the same reason.
 */
export function newsletterReorderWrites(newsletters = [], bandKey, from, to) {
  const groups = groupNewsletters(newsletters);
  const band = groups.find((g) => g.key === bandKey);
  if (!band || from === to || from < 0 || to < 0 || from >= band.rows.length || to >= band.rows.length) return [];
  const rows = [...band.rows];
  rows.splice(to, 0, rows.splice(from, 1)[0]);
  const flat = groups.flatMap((g) => (g.key === bandKey ? rows : g.rows));
  /* Renumbered rather than swapped, so a tied sort_order repairs itself
     (rule 10) — and only the rows whose number actually changed are written. */
  return flat.map((n, i) => ({ ...n, sortOrder: i })).filter((n, i) => (flat[i].sortOrder ?? null) !== i);
}

/**
 * "Cut from Tuesday's post", for a variant's meta line — and NOTHING when
 * there is no source.
 *
 * It said "Cut from a post you deleted" for a while, which was wrong on the
 * first email she added: a blank email started from Add has never been cut from
 * anything, and `source_id` being null cannot tell that apart from a post that
 * was deleted (0025 sets it null on purpose, so her writing survives). One
 * value, two stories — so it tells neither rather than the alarming one. She is
 * already told at the point it matters: the delete confirm says the cuts stay
 * and lose the link back.
 */
export function cutFromLabel(newsletter, newsletters = []) {
  if (isSource(newsletter)) return null;
  const source = (newsletters || []).find((n) => n.id === newsletter?.sourceId);
  return source ? `Cut from ${newsletterLabel(source)}` : null;
}

/* ── What Brill is told ────────────────────────────────────────────────────────
   One block, labelled fields, the same shape every other panel uses — so the
   Drafter, the parser and the sparkle all work unchanged.

   WHAT IS SPECIFIC TO THIS MODULE: a CUT is told what the source said. The
   email and the Substack post are not trims (her words), but they cannot be
   written blind either — Brill needs the piece it is cutting from, and the
   instruction to take a different angle rather than fewer paragraphs. */
export const NEWSLETTER_BLOCK = /<<<NEWSLETTER_DRAFTED>>>([\s\S]*?)<<<NEWSLETTER_END>>>/;

function blockSpecLines(block) {
  return blockKindDef(block?.blockType).fields.map((f) => `${f.block}: ${f.label.toLowerCase()} — ${f.guide}`);
}

function blockSays(block, labelFor = (f) => f.block) {
  const content = block?.content || {};
  return blockKindDef(block?.blockType).fields
    .map((f) => `${labelFor(f)}: ${String(content[f.key] || "").trim() || "(empty)"}`);
}

/** What this piece IS, before anything about one block of it. */
function newsletterLines(newsletter, { offers = [], source = null, sourceBlocks = [], pillars = [] } = {}) {
  const def = typeDef(newsletter?.type);
  const offer = (offers || []).find((o) => o.id === newsletter?.offerId);
  const lines = [
    `Piece: ${newsletterLabel(newsletter)}`,
    `Type: ${def.label} — ${def.hint}`,
    `Offer it serves: ${offer ? `${offerLine(offer)} (${offer.type || "no rung"})` : "none connected"}`,
  ];

  /* THE PILLAR IS HER WORDS, read from her locked visibility work. Listed so
     Brill can reinforce the one she picked — and so it can suggest one when
     she has not, which is what `claimsCheck` is asking her for. */
  const pillar = String(newsletter?.pillar || "").trim();
  if (pillar) lines.push(`Content pillar it reinforces: ${pillar}`);
  else if (pillars.length) lines.push(`Her content pillars (she has not picked one for this piece): ${pillars.join(" · ")}`);

  if (isSource(newsletter)) {
    lines.push(
      "This is the SOURCE — the full, canonical version. Write it long enough to be worth reading on its own.",
      "AFTER the story or the observation, it has to say things plainly enough to be QUOTED: a claim someone could",
      "lift, and the questions someone would actually type into ChatGPT or a search engine. That is what the",
      '"What this says" and "Questions this answers" sections are for. Her voice first, then the plain statement —',
      "never the other way round, and never a rewrite of her voice into marketing copy.",
    );
  } else {
    lines.push(
      `This is a CUT of a longer piece, for ${def.label.toLowerCase()}. It is NOT a trim: do not send the same`,
      "paragraphs with sentences removed. Take ONE thread of the source and open on it differently —",
      newsletter?.type === "substack"
        ? "a Substack post wants its own angle, and its readers are subscribers, not strangers."
        : "an email is read in an inbox in ten seconds, and may carry something the source does not (what she is working on, a link she liked).",
    );
    const canonical = String(newsletter?.canonicalUrl || source?.canonicalUrl || "").trim();
    if (canonical) lines.push(`It points back at the full version: ${canonical}`);
    if (source) {
      lines.push("", "THE SOURCE PIECE IT IS CUT FROM — read it, do not repeat it:");
      lines.push(`Title: ${newsletterLabel(source)}`);
      for (const b of sourceBlocks || []) {
        const text = blockToText(b);
        if (text) lines.push(`${blockName(b)}: ${text}`);
      }
    }
  }
  return lines;
}

export function buildNewsletterBlockContext({ newsletter, block, blocks = [], offers = [], source = null, sourceBlocks = [], pillars = [] }) {
  const kind = blockKindDef(block?.blockType);
  const others = (blocks || []).filter((b) => b.id !== block?.id);
  return [
    "[NEWSLETTER_CONTEXT — read only, do not reference as a conversation]",
    ...newsletterLines(newsletter, { offers, source, sourceBlocks, pillars }),
    "",
    `This section: ${blockName(block)} (${kind.label}) — ${kind.hint}`,
    "",
    "Fields to fill, one per line, using EXACTLY these labels:",
    ...blockSpecLines(block),
    "",
    "What this section says now:",
    ...blockSays(block),
    "",
    "The other sections of this piece, in order, for fit — do not rewrite them:",
    ...(others.length
      ? others.map((b) => `${blockName(b)}: ${blockWords(b) ? blockSays(b, (f) => f.label).join(" / ") : "(not written)"}`)
      : ["(none)"]),
  ].join("\n");
}

/** Every block's fields under a label unique to its place: SECTION_TWO_BODY. */
export function newsletterFields(blocks = []) {
  const out = [];
  (blocks || []).forEach((b, i) => {
    for (const f of blockKindDef(b.blockType).fields) {
      out.push({ ...f, key: `${b.id}::${f.key}`, block: `SECTION_${ordinalWord(i)}_${f.block}`, blockId: b.id, field: f.key });
    }
  });
  return out;
}

export function buildNewsletterContext({ newsletter, blocks = [], offers = [], source = null, sourceBlocks = [], pillars = [] }) {
  const fields = newsletterFields(blocks);
  const lines = [
    "[NEWSLETTER_CONTEXT — read only, do not reference as a conversation]",
    "Scope: THE WHOLE PIECE. Write as many sections as you genuinely have words for, each under its own labels, all inside ONE block.",
    ...newsletterLines(newsletter, { offers, source, sourceBlocks, pillars }),
    `Sections: ${(blocks || []).length}`,
    "",
  ];
  (blocks || []).forEach((b, i) => {
    const kind = blockKindDef(b.blockType);
    lines.push(`— Section ${i + 1} of ${blocks.length}: ${blockName(b)} (${kind.label}) — ${kind.hint}`);
    for (const f of fields.filter((x) => x.blockId === b.id)) {
      lines.push(`  ${f.block} (${f.label.toLowerCase()} — ${f.guide}): ${String(b.content?.[f.field] || "").trim() || "(empty)"}`);
    }
    lines.push("");
  });
  lines.push("Labels to answer under (omit any section you are not changing):");
  lines.push(...fields.map((f) => f.block));
  return lines.join("\n");
}

export function parseNewsletterBlockReply(text, block) {
  return parseLabelledBlock(text, blockKindDef(block?.blockType).fields, NEWSLETTER_BLOCK);
}

export function applyNewsletterBlockReply(content, parsed, block) {
  return applyLabelledBlock(content, parsed, blockKindDef(block?.blockType).fields);
}

/** { [blockId]: { field: value } } for the sections Brill wrote, or null. */
export function parseWholeNewsletterReply(text, blocks = []) {
  const fields = newsletterFields(blocks);
  const flat = parseLabelledBlock(text, fields, NEWSLETTER_BLOCK);
  if (!flat) return null;
  const out = {};
  for (const f of fields) {
    if (flat[f.key] === undefined) continue;
    (out[f.blockId] = out[f.blockId] || {})[f.field] = flat[f.key];
  }
  return Object.keys(out).length ? out : null;
}

export function stripNewsletterBlock(text) {
  return stripLabelledBlock(text, NEWSLETTER_BLOCK);
}


/* ── The stores ───────────────────────────────────────────────────────────── */
export const NEWSLETTER_FIELDS = [
  "name", "type", "sourceId", "description", "urlSlug", "imageUrl", "imageAlt",
  "canonicalUrl", "tags", "categories", "pillar", "offerId", "status", "sentAt", "sortOrder",
];

const useNewsletterRecords = createRecordStore({
  table: "newsletters",
  fields: NEWSLETTER_FIELDS,
  idPrefix: "nws",
  orderBy: "sort_order",
  ascending: true,
});

export const BLOCK_FIELDS = ["newsletterId", "blockType", "name", "sortOrder", "content"];

/* NOT `createSectionStore`. CLAUDE.md says a second composed record should be
   one call to it, and that is no longer true of the code: it imports
   `kindDef` and `emptyContent` from websiteSectionKinds, so it can only ever
   build WEBSITE blocks (its rows also carry `source` and a per-block status,
   both website-only). Presentations hit the same wall and wrote its own. Said
   once here rather than quietly diverging. */
const useBlockRecords = createRecordStore({
  table: "newsletter_blocks",
  fields: BLOCK_FIELDS,
  idPrefix: "nwb",
  /* No clerk_user_id on a block: it belongs to its newsletter, and 0025's
     policy walks that key up to her. */
  ownerColumn: null,
  orderBy: "sort_order",
  ascending: true,
});

export function useNewsletters() {
  const {
    rows: newsletters, loading, error, save: saveNewsletter, remove: removeNewsletter,
    clearError: clearNewslettersError, newId: newNewsletterId,
  } = useNewsletterRecords();
  const {
    rows: blockRows, loading: blocksLoading, error: blocksError,
    save: saveBlock, remove: removeBlockRow, clearError: clearBlocksError, newId: newBlockId,
  } = useBlockRecords();

  /* Every block of every newsletter in one query — the table shows a word count
     and a dot per block on each row, so fetching per record would be a request
     inside a render loop. Sorted here, because the store puts a row saved this
     session at the head of `rows`. */
  const byNewsletter = useMemo(() => {
    const map = new Map();
    for (const b of blockRows || []) {
      const list = map.get(b.newsletterId) || [];
      list.push(b);
      map.set(b.newsletterId, list);
    }
    for (const list of map.values()) list.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    return map;
  }, [blockRows]);

  const blocksFor = useCallback((id) => byNewsletter.get(id) || [], [byNewsletter]);

  const addBlock = useCallback(async (newsletterId, kind = CUSTOM_KIND) => {
    const have = blocksFor(newsletterId);
    const last = have.length ? (have[have.length - 1].sortOrder ?? have.length - 1) : -1;
    return saveBlock({
      id: newBlockId(), newsletterId,
      blockType: blockKindDef(kind).id, name: "", sortOrder: last + 1,
      content: emptyBlockContent(kind),
    });
  }, [blocksFor, saveBlock, newBlockId]);

  /** A new piece, with the blocks its type starts with, so she lands on
   *  something to write rather than an empty record. */
  const addNewsletter = useCallback(async (type = "blog", extra = {}) => {
    const def = typeDef(type);
    const last = newsletters.length ? Math.max(...newsletters.map((n) => n.sortOrder ?? 0)) : -1;
    const saved = await saveNewsletter({
      id: newNewsletterId(),
      name: "", type: def.id, sourceId: null,
      description: null, urlSlug: null, imageUrl: null, imageAlt: null, canonicalUrl: null,
      tags: [], categories: [], pillar: null, offerId: null,
      status: "draft", sentAt: null, sortOrder: last + 1,
      ...extra,
    });
    if (!saved) return null;
    let order = 0;
    for (const kindId of def.blocks) {
      await saveBlock({
        id: newBlockId(), newsletterId: saved.id,
        blockType: kindId, name: "", sortOrder: order++,
        content: emptyBlockContent(kindId),
      });
    }
    return saved;
  }, [newsletters, saveNewsletter, newNewsletterId, saveBlock, newBlockId]);

  /**
   * A CUT of a source piece. Her shape: one source, three types, each variant
   * carrying `sourceId` and the blog's URL as its canonical link.
   *
   * It creates the variant EMPTY — with its own type's blocks — and Brill
   * writes the cut in the chat. It deliberately does not copy the blog's words
   * across: "not just a trim" was the whole point, and a pre-filled copy is
   * exactly the trim she said she did not want.
   */
  const makeVariant = useCallback(async (source, type) => {
    if (!source?.id || !VARIANT_TYPES.includes(type)) return null;
    return addNewsletter(type, {
      sourceId: source.id,
      /* The blog's public address if she has written one — GHL's own
         `canonicalLink` field, so it pushes as-is later. */
      canonicalUrl: source.canonicalUrl || null,
      pillar: source.pillar || null,
      offerId: source.offerId || null,
      name: source.name ? `${source.name} — ${typeDef(type).label.toLowerCase()}` : "",
    });
  }, [addNewsletter]);

  /** The cuts made from a source, newest last. */
  const variantsOf = useCallback(
    (id) => (newsletters || []).filter((n) => n.sourceId === id),
    [newsletters],
  );

  const removeBlock = useCallback((id) => removeBlockRow(id), [removeBlockRow]);

  /** Delete a piece and its blocks. The table cascades, but the local list
   *  would keep them until a reload. A variant's `source_id` is set null by
   *  0025 rather than cascading: a cut she has edited is her work. */
  const deleteNewsletter = useCallback(async (id) => {
    for (const b of blocksFor(id)) {
      if (!(await removeBlockRow(b.id))) return false;
    }
    return removeNewsletter(id);
  }, [blocksFor, removeBlockRow, removeNewsletter]);

  const reorderBlocks = useCallback(async (newsletterId, from, to) => {
    const writes = reorderWrites(blocksFor(newsletterId), from, to);
    let ok = true;
    // Sequential: the store replaces rows one at a time and racing them can
    // interleave writes onto a stale copy of the list.
    for (const row of writes) if (!(await saveBlock(row))) ok = false;
    return ok;
  }, [blocksFor, saveBlock]);

  /* Banded, like Scripts: the table and the rail both hand over a move inside
     one type's band, never a position in the flat list. */
  const reorderNewsletters = useCallback(async (bandKey, from, to) => {
    let ok = true;
    for (const row of newsletterReorderWrites(newsletters, bandKey, from, to)) {
      if (!(await saveNewsletter(row))) ok = false;
    }
    return ok;
  }, [newsletters, saveNewsletter]);

  return {
    newsletters, loading, error: error || blocksError, blocksLoading,
    blocksFor, variantsOf,
    saveNewsletter, addNewsletter, makeVariant, deleteNewsletter, reorderNewsletters,
    saveBlock, addBlock, removeBlock, reorderBlocks,
    clearError: () => { clearNewslettersError(); clearBlocksError(); },
  };
}
